/**
 * LocalStorage Migration Utility
 * 
 * 將舊格式的 localStorage（多個 key）遷移到新格式（單一 key savedStations）
 * 遷移完成後自動刪除舊的 key 以節省空間
 */

import logger from './logger';

// 舊格式的 key 列表
const OLD_STORAGE_KEYS = [
  'EAL_SaveStn',
  'TKL_SaveStn',
  'TCL_SaveStn',
  'AEL_SaveStn',
  'TML_SaveStn',
  'SIL_SaveStn',
  'TWL_SaveStn',
  'ISL_SaveStn',
  'KTL_SaveStn',
  'DRL_SaveStn',
  'LrtSaveStn',
  'MTRBus_SaveStn',
  // HKTram 已移除，但需要處理舊資料
  'HKTramSaveStn'
];

// 新格式的 key
const NEW_STORAGE_KEY = 'savedStations';

// Migration 標記 key
const MIGRATION_FLAG_KEY = 'storageMigrationCompleted';

/**
 * 檢查是否需要執行 migration
 */
export const needsMigration = () => {
  // 如果已經完成 migration，則不需要
  if (localStorage.getItem(MIGRATION_FLAG_KEY) === 'true') {
    return false;
  }

  // 如果新格式已存在，則不需要
  if (localStorage.getItem(NEW_STORAGE_KEY)) {
    return false;
  }

  // 檢查是否有舊格式的資料
  return OLD_STORAGE_KEYS.some(key => {
    const value = localStorage.getItem(key);
    return value && value !== '[]' && value !== 'null';
  });
};

/**
 * 執行 migration
 * 將舊格式轉換為新格式
 */
export const performMigration = () => {
  if (!needsMigration()) {
    return null;
  }

  const newFormat = {};

  // 遍歷所有舊格式的 key
  OLD_STORAGE_KEYS.forEach(oldKey => {
    try {
      const oldValue = localStorage.getItem(oldKey);
      
      if (oldValue) {
        const parsed = JSON.parse(oldValue);
        
        // 如果解析成功且是陣列
        if (Array.isArray(parsed) && parsed.length > 0) {
          // 將 key 轉換為新格式的路線代碼
          const lineCode = convertKeyToLineCode(oldKey);
          
          if (lineCode) {
            // MTRBus 需要特殊處理：按路線分組
            if (lineCode === 'MTRBus') {
              if (!newFormat[lineCode]) {
                newFormat[lineCode] = {};
              }
              
              // 將舊格式的車站 ID（如 "K12-U010"）按路線分組
              parsed.forEach(stationId => {
                // 從車站 ID 提取路線（如 "K12-U010" -> "K12"）
                const routeMatch = stationId.match(/^([A-Z0-9]+)-/);
                if (routeMatch) {
                  const route = routeMatch[1];
                  if (!newFormat[lineCode][route]) {
                    newFormat[lineCode][route] = [];
                  }
                  if (!newFormat[lineCode][route].includes(stationId)) {
                    newFormat[lineCode][route].push(stationId);
                  }
                }
              });
            } else {
              // 其他路線類型：直接使用陣列
              if (newFormat[lineCode]) {
                // 合併並去重
                newFormat[lineCode] = [...new Set([...newFormat[lineCode], ...parsed])];
              } else {
                newFormat[lineCode] = parsed;
              }
            }
          }
        }
      }
    } catch (error) {
      logger.warn(`Failed to migrate ${oldKey}:`, error);
    }
  });

  // 如果有資料，則儲存新格式
  if (Object.keys(newFormat).length > 0) {
    localStorage.setItem(NEW_STORAGE_KEY, JSON.stringify(newFormat));
  } else {
    // 即使沒有資料，也建立空物件
    localStorage.setItem(NEW_STORAGE_KEY, JSON.stringify({}));
  }

  // 刪除舊的 key
  deleteOldStorageKeys();

  // 標記 migration 完成
  localStorage.setItem(MIGRATION_FLAG_KEY, 'true');

  return newFormat;
};

/**
 * 將舊的 key 轉換為新格式的路線代碼
 */
const convertKeyToLineCode = (oldKey) => {
  const keyMap = {
    'EAL_SaveStn': 'EAL',
    'TKL_SaveStn': 'TKL',
    'TCL_SaveStn': 'TCL',
    'AEL_SaveStn': 'AEL',
    'TML_SaveStn': 'TML',
    'SIL_SaveStn': 'SIL',
    'TWL_SaveStn': 'TWL',
    'ISL_SaveStn': 'ISL',
    'KTL_SaveStn': 'KTL',
    'DRL_SaveStn': 'DRL',
    'LrtSaveStn': 'LRT',
    'MTRBus_SaveStn': 'MTRBus',
    // HKTram 已移除，但處理舊資料時可以忽略或記錄
    'HKTramSaveStn': null // 不遷移，因為功能已移除
  };

  return keyMap[oldKey] || null;
};

/**
 * 刪除舊的 localStorage key
 */
const deleteOldStorageKeys = () => {
  OLD_STORAGE_KEYS.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.warn(`Failed to delete old key ${key}:`, error);
    }
  });
};

/**
 * 獲取遷移後的資料
 */
export const getMigratedData = () => {
  try {
    const data = localStorage.getItem(NEW_STORAGE_KEY);
    if (data) {
      return JSON.parse(data);
    }
  } catch (error) {
    logger.error('Failed to get migrated data:', error);
  }
  return {};
};

